import { decodeFrame, type FrameHeader } from '@br/protocol';

export interface FrameInfo {
  header: FrameHeader;
  bitmap: ImageBitmap;
  receivedAt: number;
}

const MAX_DECODING_PER_TAB = 2;

/** Where a tab's frame sequence stands: `epoch` bumps whenever the sender's seq starts over. */
interface Cursor {
  epoch: number;
  seq: number;
}

function isBehind(a: Cursor, b: Cursor): boolean {
  return a.epoch < b.epoch || (a.epoch === b.epoch && a.seq <= b.seq);
}

/**
 * Latest decoded frame per tab. Decodes that finish out of order are dropped so the picture never
 * steps backwards. The socket delivers frames in order, so a seq that goes *down* is not a stale
 * frame but a stream that started over (node reconnect, screencast restart); it opens a new epoch
 * instead of being dropped. Listeners are notified per tab so the canvas can redraw without React
 * re-rendering on every frame.
 */
export class FrameStore {
  private readonly frames = new Map<string, FrameInfo & { cursor: Cursor }>();
  private readonly cursors = new Map<string, Cursor>();
  private readonly decoding = new Map<string, number>();
  private readonly listeners = new Map<string, Set<() => void>>();
  private disposed = false;

  /** Accepts a raw binary websocket message; ignores anything that is not a frame. */
  async push(buf: Uint8Array): Promise<void> {
    if (this.disposed) return;
    let decoded: ReturnType<typeof decodeFrame>;
    try {
      decoded = decodeFrame(buf);
    } catch {
      return;
    }
    const { header, payload } = decoded;
    const tabId = header.tabId;
    const last = this.cursors.get(tabId);
    let cursor: Cursor;
    if (!last) cursor = { epoch: 0, seq: header.seq };
    else if (header.seq > last.seq) cursor = { epoch: last.epoch, seq: header.seq };
    else if (header.seq === last.seq)
      return; // duplicate
    else cursor = { epoch: last.epoch + 1, seq: header.seq }; // sender restarted its counter
    this.cursors.set(tabId, cursor);
    const inflight = this.decoding.get(tabId) ?? 0;
    if (inflight >= MAX_DECODING_PER_TAB) return; // decoder cannot keep up: skip this frame
    this.decoding.set(tabId, inflight + 1);
    try {
      const blob = new Blob([payload as Uint8Array<ArrayBuffer>], { type: header.mime });
      const bitmap = await createImageBitmap(blob);
      const shown = this.frames.get(tabId);
      if (this.disposed || (shown && isBehind(cursor, shown.cursor))) {
        bitmap.close();
        return;
      }
      shown?.bitmap.close();
      this.frames.set(tabId, { header, bitmap, receivedAt: Date.now(), cursor });
      this.emit(tabId);
    } catch {
      /* undecodable image: skip */
    } finally {
      this.decoding.set(tabId, Math.max(0, (this.decoding.get(tabId) ?? 1) - 1));
    }
  }

  get(tabId: string): FrameInfo | undefined {
    return this.frames.get(tabId);
  }

  subscribe(tabId: string, listener: () => void): () => void {
    let set = this.listeners.get(tabId);
    if (!set) {
      set = new Set();
      this.listeners.set(tabId, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  /** Forgets a tab (closed) and releases its bitmap. */
  drop(tabId: string): void {
    this.frames.get(tabId)?.bitmap.close();
    this.frames.delete(tabId);
    this.cursors.delete(tabId);
    this.emit(tabId);
  }

  dispose(): void {
    this.disposed = true;
    for (const f of this.frames.values()) f.bitmap.close();
    this.frames.clear();
    this.listeners.clear();
  }

  private emit(tabId: string) {
    const set = this.listeners.get(tabId);
    if (!set) return;
    for (const l of set) l();
  }
}
